/**
 * Post-process lead research so role, geography, phones, and the emergency
 * site signal stay inside verified public text. Prompt rules alone still
 * invented titles and "across Norway".
 */
import { resolveLeadFacingRole, isGenericLeadRole } from '../../lib/identity-briefing-merge.js'
import { extractGroundedPhones, normalizePhoneDigits } from '../../lib/grounded-phones.js'
import type { ResearchResult } from './lead-research.js'
import type { RegistryRoleHolder } from './registry-lookup.js'

// Hyphenated "24-TIMERS" and "døgnåpen" are the homepage forms. No /g — .test lastIndex would stick.
const EMERGENCY_SITE_SIGNAL =
  /\b(?:24\s*[-/]?\s*(?:7|timers?|t(?:imer)?|h(?:ours?)?)|døgnvakt|dognvakt|døgnåp(?:en|ent|ne)|dognap(?:en|ent|ne)|døgnet rundt|akutt[\s-]*vakt|akutt|emergency(?:\s+call(?:out)?|\s+service)?|utrykning|vakttelefon)\b/i

const PHONE_CANDIDATE_RE = /(?:\+47[\s.-]?)?(?<!\d)(\d(?:[\s.-]?\d){7})(?!\d)/g

export function hasEmergencySiteSignal(text: string | null | undefined): boolean {
  return EMERGENCY_SITE_SIGNAL.test(text || '')
}

function normalizePersonName(value?: string | null): string {
  return (value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** First and last token. A single given name never matches a registry holder. */
function namesMatch(visitorName: string, holderName: string): boolean {
  const visitor = normalizePersonName(visitorName).split(' ').filter(Boolean)
  const holder = normalizePersonName(holderName).split(' ').filter(Boolean)
  if (visitor.length < 2 || holder.length < 2) return false
  return visitor[0] === holder[0] && visitor[visitor.length - 1] === holder[holder.length - 1]
}

const NAMED_OFFICER = /^(.{2,80}?)\s+[—–-]\s+(.+)$/
const ROLLER_TAIL = /\.?\s*roller i norsk næringsliv\.?$/i
const ROLE_ATTRIBUTION =
  /\b(daglig leder|styreleder|styremedlem|founder|co-founder|owner|eier|ceo|cto|cfo|coo|managing director|president|partner|innehaver|rørlegger|plumber)\b/i
const ROLE_NEGATION = /\b(is not|isn't|isnt|are not|not this|ikke)\b/i

function tidy(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

function stripRollerTail(value: string): string {
  return tidy(value.replace(ROLLER_TAIL, '').replace(/[.\s]+$/, ''))
}

function parseNamedOfficer(claim: string): { name: string; role: string } | null {
  const match = tidy(claim).match(NAMED_OFFICER)
  const name = match?.[1]?.trim() || ''
  const role = match?.[2] ? stripRollerTail(match[2]) : ''
  if (!name || !role) return null
  return { name, role }
}

function mentionsVisitor(claim: string, visitorName: string): boolean {
  const visitor = normalizePersonName(visitorName).split(' ').filter(Boolean)
  if (visitor.length < 2) return false
  const tokens = normalizePersonName(claim).split(' ').filter(Boolean)
  const firstAt = tokens.indexOf(visitor[0] || '')
  if (firstAt < 0) return false
  return tokens.slice(firstAt + 1).includes(visitor[visitor.length - 1] || '')
}

function sameRole(left: string, right: string): boolean {
  return normalizePersonName(stripRollerTail(left)) === normalizePersonName(stripRollerTail(right))
}

/**
 * Person ROLE on the client card. A Brreg officer is the visitor's role only
 * when the holder name matches. Company roller text stays off that field.
 */
export function visitorFacingRole(input: {
  visitorName?: string | null
  personRole?: string | null
  roleSignals?: Array<{ claim?: string | null }> | null
}): string | undefined {
  const visitor = tidy(input.visitorName || '')
  const claims = (input.roleSignals || []).map(row => tidy(row.claim || '')).filter(Boolean)

  for (const claim of claims) {
    const officer = parseNamedOfficer(claim)
    if (officer && namesMatch(visitor, officer.name)) return officer.role
  }

  for (const claim of claims) {
    if (parseNamedOfficer(claim)) continue
    if (!mentionsVisitor(claim, visitor) || ROLE_NEGATION.test(claim)) continue
    const roleWord = claim.match(ROLE_ATTRIBUTION)?.[0]
    if (roleWord) return roleWord
  }

  const personRole = input.personRole ? stripRollerTail(tidy(input.personRole)) : ''
  if (!personRole || /^unknown$/i.test(personRole)) return undefined

  const embedded = parseNamedOfficer(input.personRole || '')
  if (embedded) return namesMatch(visitor, embedded.name) ? embedded.role : undefined

  const unmatched = claims
    .map(parseNamedOfficer)
    .filter((row): row is { name: string; role: string } => row !== null && !namesMatch(visitor, row.name))
  if (unmatched.some(row => sameRole(row.role, personRole))) return undefined

  const echoesUnnamedOfficer = claims.some(claim => {
    if (parseNamedOfficer(claim) || mentionsVisitor(claim, visitor)) return false
    const titled = claim.match(ROLE_ATTRIBUTION)?.[0] || claim
    return sameRole(titled, personRole)
  })
  if (echoesUnnamedOfficer) return undefined

  return personRole
}

export function groundVisitorRole(input: {
  visitorName?: string | null
  holders?: RegistryRoleHolder[] | null
  modelRole?: string | null
  personRole?: string | null
}): string {
  const holders = (input.holders || []).filter(holder => holder.name.trim() && holder.role.trim())
  const visitorName = input.visitorName?.trim() || ''
  if (holders.length > 0) {
    if (!visitorName) return 'Unknown'
    const match = holders.find(holder => namesMatch(visitorName, holder.name))
    return match?.role?.trim() || 'Unknown'
  }
  const resolved = resolveLeadFacingRole({
    researchRole: input.modelRole,
    personRole: input.personRole,
  })
  if (!resolved || isGenericLeadRole(resolved)) return 'Unknown'
  return resolved
}

/** Rewrite national overclaims only when a registered place exists. */
export function constrainCompanyGeography(summary: string, place?: string | null): string {
  const where = place?.replace(/\s+/g, ' ').trim()
  if (!summary || !where) return summary
  return summary
    .replace(/\b(?:across|throughout|all over|nationwide|nationally)\s+(?:norway|norge)\b/gi, `in ${where}`)
    .replace(/\b(?:over hele|i hele|på tvers av)\s+(?:norge|landet)\b/gi, `i ${where}`)
}

export function stripPhonesNotInSources(summary: string, sources: string): string {
  if (!summary) return summary
  const allowed = new Set(extractGroundedPhones(sources))
  return summary
    .replace(PHONE_CANDIDATE_RE, (match, group: string) => {
      let digits = normalizePhoneDigits(group || match)
      if (digits.startsWith('47') && digits.length === 10) digits = digits.slice(2)
      if (digits.length !== 8) return match
      if (allowed.has(digits)) return match
      return ''
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([,.])/g, '$1')
    .trim()
}

/** Keep an emergency clause only when the cited/site text actually says it. */
export function reconcileEmergencyClaim(summary: string, siteText: string): string {
  if (hasEmergencySiteSignal(siteText)) {
    if (hasEmergencySiteSignal(summary)) return summary
    const clause = 'Public pages mention 24h akuttvakt / emergency callout coverage.'
    return summary.trim() ? `${summary.trim()} ${clause}` : clause
  }
  if (!hasEmergencySiteSignal(summary)) return summary
  return summary
    .split(/(?<=[.!])\s+/)
    .filter(sentence => !hasEmergencySiteSignal(sentence))
    .join(' ')
    .trim()
}

export function applyGroundedResearchClaims(
  result: ResearchResult,
  input: {
    holders?: RegistryRoleHolder[] | null
    place?: string | null
    sourceTexts?: Array<string | null | undefined>
    siteText?: string | null
  },
): ResearchResult {
  const sourceBlob = (input.sourceTexts || []).filter((part): part is string => Boolean(part?.trim())).join('\n')
  const siteText = input.siteText?.trim() || sourceBlob
  const role = groundVisitorRole({
    visitorName: result.person?.fullName,
    holders: input.holders ?? null,
    modelRole: result.role,
    personRole: result.person?.role ?? null,
  })

  let summary = result.company?.summary || ''
  summary = reconcileEmergencyClaim(summary, siteText)
  summary = constrainCompanyGeography(summary, input.place)
  summary = stripPhonesNotInSources(summary, sourceBlob)

  const { role: _droppedRole, ...personRest } = result.person
  const person = role === 'Unknown' ? personRest : { ...personRest, role }

  const painPoints = (result.strategic?.pain_points || []).map(point =>
    stripPhonesNotInSources(constrainCompanyGeography(point, input.place), sourceBlob),
  )

  return {
    ...result,
    role,
    company: {
      ...result.company,
      ...(summary ? { summary } : {}),
    },
    person,
    ...(result.strategic
      ? {
          strategic: {
            ...result.strategic,
            pain_points: painPoints,
          },
        }
      : {}),
  }
}
